"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles,
  Loader2,
} from "lucide-react";
import { useCallStore } from "@/store/call-store";
import type { Recommendation } from "@/lib/types";
import { cn } from "@/lib/utils";
import { TerminalBootSequence } from "@/components/ui/terminal-boot";

interface AiCoachProps {
  className?: string;
  isCoaching?: boolean;
}

export function AiCoach({ className, isCoaching }: AiCoachProps) {
  const rec = useCallStore((s) => s.recommendation);

  return (
    <div className={cn("flex min-h-0 flex-col bg-card card overflow-hidden", className)}>
      <header className="flex h-12 items-center gap-2 border-b border-line px-4 bg-card-muted/50">
        {isCoaching ? (
          <Loader2 className="size-4 text-primary animate-spin" />
        ) : (
          <Sparkles className="size-4 text-primary" />
        )}
        <h2 className="text-sm font-semibold">AI Coach</h2>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4 relative">
        {isCoaching && !rec && (
          <div className="absolute inset-0 flex items-center justify-center flex-col gap-4 font-mono">
             <div className="flex items-center gap-2 text-primary">
                <span className="size-2 bg-primary animate-pulse shadow-[0_0_10px_currentColor]" style={{ animationDelay: "0ms" }} />
                <span className="size-2 bg-primary animate-pulse shadow-[0_0_10px_currentColor]" style={{ animationDelay: "150ms" }} />
                <span className="size-2 bg-primary animate-pulse shadow-[0_0_10px_currentColor]" style={{ animationDelay: "300ms" }} />
             </div>
             <p className="text-xs font-bold text-text-secondary tracking-widest uppercase phosphor">Analyzing Tensor Stream...</p>
          </div>
        )}
        
        {!isCoaching && !rec && (
           <TerminalBootSequence 
            lines={[
              "varta analyze --continuous",
              "[ai] SPINNING UP NEURAL ENGINE...",
              "[ai] ALLOCATING TENSORS... OK",
              "[ai] CONNECTING TO GROQ INFERENCE CLUSTER... OK",
              "WAITING FOR INTENT TRIGGERS..."
            ]} 
            promptColor="text-purple"
          />
        )}

        {rec && (
          <div className={cn("transition-opacity", isCoaching && "opacity-50")}>
            <CoachBody key={rec.intent} rec={rec} />
          </div>
        )}
      </div>
    </div>
  );
}

function CoachBody({ rec }: { rec: Recommendation }) {
  return (
    <div className="flex flex-col gap-4">
      {/* Customer intent */}
      <div>
        <p className="text-[11px] font-bold text-text-tertiary uppercase tracking-wider mb-1">Detected Intent</p>
        <h3 className="text-base font-semibold tracking-tight text-balance text-text">
          {rec.intent}
        </h3>
      </div>

      {/* Recommended response */}
      <motion.div
        key={rec.intent}
        initial={{ opacity: 0, y: 10, filter: 'blur(8px)', scale: 0.98 }}
        animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-xl border border-primary/20 bg-primary-soft p-4 relative overflow-hidden shadow-inner"
      >
        <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
        <p className="text-[11px] font-bold text-primary uppercase tracking-wider mb-2">Suggested Response</p>
        <p className="text-[15px] leading-relaxed font-medium text-balance">
          {rec.response}
        </p>
      </motion.div>

      {/* Alternative */}
      {rec.alternative && (
        <div className="rounded-xl border border-line bg-card-muted p-4">
          <p className="text-[11px] font-bold text-text-tertiary uppercase tracking-wider mb-2">Alternative Approach</p>
          <p className="text-sm text-text-secondary leading-relaxed">
            {rec.alternative}
          </p>
        </div>
      )}

      {/* Next best question */}
      {rec.nextBestQuestion && (
        <div className="rounded-xl border border-purple/20 bg-purple-soft p-4 mt-2">
          <p className="text-[11px] font-bold text-purple uppercase tracking-wider mb-2">Next Best Question</p>
          <p className="text-sm font-medium text-text">
            {rec.nextBestQuestion}
          </p>
        </div>
      )}
    </div>
  );
}
